import styled from '@emotion/styled';
import { motion } from 'framer-motion';

const ExhibitionContainer = styled.div<{ variant?: 'dark' | 'paper' }>`
  min-height: 100vh;
  min-height: 100dvh;
  background-color: transparent;
  color: ${props => props.variant === 'paper' ? '#2c2a26' : '#fff'};
  /* Mobile-first */
  padding: 1.5rem 0.75rem;
  padding-top: max(1.5rem, env(safe-area-inset-top));
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (min-width: 640px) {
    padding: 3rem 1rem;
  }

  @media (min-width: 768px) {
    padding: 6rem 2rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  max-width: 800px;
  width: 100%;
  padding: 0 0.5rem;

  @media (min-width: 640px) {
    margin-bottom: 3rem;
  }

  @media (min-width: 768px) {
    margin-bottom: 6rem;
    padding: 0;
  }
`;

const Presents = styled(motion.div)<{ theme?: 'dark' | 'paper' }>`
  font-size: 0.8rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 1rem;
  opacity: ${props => props.theme === 'paper' ? 0.7 : 0.9};
  color: ${props => props.theme === 'paper' ? '#4a4540' : 'inherit'};

  @media (min-width: 640px) {
    font-size: 0.9rem;
  }

  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const Title = styled(motion.h1)<{ variant?: 'dark' | 'paper' }>`
  /* Mobile-first */
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
  text-align: center;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-family: ${props => props.variant === 'paper' 
    ? 'Georgia, "Times New Roman", serif' 
    : 'inherit'};
  color: inherit;

  @media (min-width: 640px) {
    font-size: 2.5rem;
  }

  @media (min-width: 768px) {
    font-size: 4rem;
    margin-bottom: 1.5rem;
  }
`;

const Subtitle = styled(motion.h2)<{ theme?: 'dark' | 'paper' }>`
  font-size: 0.875rem;
  font-weight: 300;
  opacity: ${props => props.theme === 'paper' ? 0.8 : 0.9};
  margin-bottom: 1rem;
  color: ${props => props.theme === 'paper' ? '#4a4540' : 'inherit'};
  line-height: 1.6;
  padding: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (min-width: 640px) {
    font-size: 1rem;
  }

  @media (min-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 2rem;
  }
`;

const DateInfo = styled(motion.div)<{ theme?: 'dark' | 'paper' }>`
  font-size: 0.8rem;
  opacity: ${props => props.theme === 'paper' ? 0.75 : 0.9};
  line-height: 1.6;
  color: ${props => props.theme === 'paper' ? '#5a5550' : 'inherit'};
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  @media (min-width: 640px) {
    font-size: 0.9rem;
  }

  @media (min-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const OpeningInfo = styled.div`
  margin-bottom: 0.5rem;
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  line-height: 1.8;
  padding: 0 0.5rem;
  width: 100%;

  @media (min-width: 640px) {
    padding: 0 1rem;
  }

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

interface ExhibitionLayoutProps {
  title: string;
  subtitle: string;
  openingDate: string;
  closingDate: string;
  children: React.ReactNode;
  background?: React.ReactNode;
  navigation?: React.ReactNode;
  variant?: 'dark' | 'paper';
}

export const ExhibitionLayout = ({
  title,
  subtitle,
  openingDate,
  closingDate,
  children,
  background,
  navigation,
  variant = 'dark',
}: ExhibitionLayoutProps) => {
  return (
    <>
      {background}
      <ExhibitionContainer variant={variant}>
        <Header>
          <Presents
            theme={variant}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Déjà Gallery Presents
          </Presents>
          <Title
            variant={variant}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            "{title}"
          </Title>
          <DateInfo
            theme={variant}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <OpeningInfo>
              {openingDate}
              <br />
              Opening Reception from 5PM
            </OpeningInfo>
            Continuing until {closingDate}
          </DateInfo>
          <Subtitle
            theme={variant}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {subtitle}
          </Subtitle>
        </Header>

        {navigation}
        
        <Content>
          {children}
        </Content>
      </ExhibitionContainer>
    </>
  );
}; 